/* eslint-disable jsx-a11y/anchor-is-valid */
import {
    Fragment,
    useState,
    TouchEvent,
    KeyboardEvent,
    useRef,
    useEffect
} from "react";

// eslint-disable-next-line no-restricted-imports
import { css } from "@emotion/react";

import { media, UNSAFE_media } from "@atlaskit/primitives/responsive";
import { token } from "@atlaskit/tokens";

import { ProductKeyTypes } from "@/libraries/wpl/foundations/src";

import { Tracking } from "../../../../analytics/atl-analytics/tracking";
import { useOutsideEvent } from "../../../../utils/useOutsideEvent";

type ProductNavDropdownItem = {
    label: string;
    labelEnglish?: string;
    href: string;
};

export type ProductNavDropdownItemGroup = {
    header: string;
    items: Array<ProductNavDropdownItem>;
};

export interface ProductNavDropdownProps {
    label: string;
    isMobile?: boolean;
    groups?: Array<ProductNavDropdownItemGroup>;
    dataProductKey?: ProductKeyTypes;
}

const parentItemStyle = css`
    position: relative;
    top: 3px;
    display: flex;
    align-items: center;
    font-size: 1rem;
    line-height: 1;
    color: #0052cc;

    &:hover {
        color: #0065ff;

        &::after {
            ${media.above.md} {
                transform: translate3d(0, 2px, 0) rotate(90deg);
                transition: transform 250ms;
            }
        }
    }

    &::after {
        ${media.above.md} {
            background: url("https://wac-cdn.atlassian.com/assets/img/wac/arrow-right-color.svg")
                no-repeat scroll center center;
            background-size: auto;
            background-size: contain;
            content: "";
            display: inline-block;
            margin-left: 6px;
            width: 12px;
            height: 12px;
            transform: translate3d(0, 0, 0) rotate(90deg);
            transition: transform 250ms;
        }

        ${UNSAFE_media.below.md} {
            content: "";
            width: 6px;
            height: 6px;
            border-right: 2px solid;
            border-bottom: 2px solid;
            color: inherit;
            top: 37%;
            margin-left: 10px;
            transition: transform 300ms;
            transform: rotate(45deg);
            transform-origin: center center;
        }
    }

    &.open::after {
        ${UNSAFE_media.below.md} {
            transform: rotate(-135deg) translateY(-37%) translateX(-25%);
        }
    }

    ${media.above.md} {
        margin: 0 ${token("space.200")};
        padding: ${token("space.200")} 0;
        box-sizing: border-box;
        border-bottom: 3px solid white;
    }

    ${UNSAFE_media.below.md} {
        width: 100%;
        padding: ${token("space.200")} 10px;
        margin: 0 ${token("space.200")};
        right: 10px;
    }
`;

const dropdownStyle = css`
    background-color: #fff;
    border: 1px solid #f4f5f7;
    border-radius: 4px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.25);
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translate(-50%, -8px);
    z-index: 10;
`;

const listStyle = css`
    list-style: none;
    padding-left: 0;
    padding-top: 5px;
    padding-bottom: 5px;
    position: relative;
    z-index: 1;

    ::before {
        background-color: #fff;
        top: 0;
        z-index: 0;
        bottom: 100%;
        left: 50%;
        border: 9px solid rgba(0, 0, 0, 0);
        content: " ";
        height: 0;
        width: 0;
        position: absolute;
        pointer-events: none;
        transform: translateX(-50%);
    }

    ::after {
        filter: drop-shadow(0 0 3px rgba(0, 0, 0, 0.25));
        z-index: -1;
        bottom: 100%;
        left: 50%;
        border: 8px solid rgba(0, 0, 0, 0);
        content: " ";
        height: 0;
        width: 0;
        position: absolute;
        pointer-events: none;
        border-bottom-color: #fff;
        transform: translateX(-50%);
    }
`;

const headerStyle = css`
    display: block;
    padding: 4px 30px;
    white-space: nowrap;
    text-decoration: none;
    font-size: 1rem;
    color: #253858;
    margin: 12px 0 0 0;
    text-transform: uppercase;
    font-weight: 700;
    line-height: 1.25;
    letter-spacing: 1px;
`;

const listItemStyle = css`
    position: relative;
    margin-top: 12px;
    margin-bottom: 12px;
`;

const linkStyle = css`
    font-size: 1rem;
    line-height: 1.11111111;
    display: block;
    padding: 4px 30px;
    white-space: nowrap;
    text-decoration: none;
    color: #0052cc;

    :hover {
        border-bottom-color: #000000;
        background-color: #0052cc;
        color: #ffffff;
    }
`;

const mobileDropdownStyle = css`
    ul {
        padding: 0;
    }
`;

export const ProductNavDropdown = ({
    label,
    isMobile = false,
    groups,
    dataProductKey = "jiraSoftware"
}: ProductNavDropdownProps) => {
    const [isDropdownVisible, setDropdownVisible] = useState(false);
    const [isMobileDropdownVisible, setMobileDropdownVisible] = useState(false);

    const dropdownRef = useRef<HTMLDivElement>(null);
    useOutsideEvent(dropdownRef, "click", () => setDropdownVisible(false));
    useOutsideEvent(dropdownRef, "focusin", () => setDropdownVisible(false));

    const innerRef = useRef<HTMLDivElement>(null);
    useOutsideEvent(innerRef, "click", () => setDropdownVisible(false));
    useOutsideEvent(innerRef, "focusin", () => setDropdownVisible(false));

    const handleTouchEnd = (event: TouchEvent<HTMLDivElement>) => {
        event.preventDefault();
        setMobileDropdownVisible((state) => !state);
    };

    const handleClick = () => setMobileDropdownVisible((state) => !state);

    const handleMouseEnter = () => setDropdownVisible(true);

    const handleMouseLeave = () => setDropdownVisible(false);

    const handleKeyUp = (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key === "Enter") {
            setDropdownVisible((state) => !state);
        }
    };

    useEffect(() => {
        if (isDropdownVisible || isMobileDropdownVisible) {
            Tracking.autoTrackLinks(true);
        }
    }, [isDropdownVisible, isMobileDropdownVisible]);

    const renderGroups = (dataProductKey: ProductKeyTypes) =>
        groups?.map(({ header, items }, groupIndex) => (
            <Fragment key={`${header}-${groupIndex}`}>
                <li>
                    <h5 css={headerStyle}>{header}</h5>
                </li>
                {items.map(
                    (
                        {
                            label: itemLabel,
                            labelEnglish: itemLabelEnglish,
                            href
                        },
                        index
                    ) => (
                        <li key={`${itemLabel}-${index}`} css={listItemStyle}>
                            <a
                                href={href}
                                css={linkStyle}
                                id={`dropdown-${label.toLowerCase()}-${index}`}
                                data-event="clicked"
                                data-event-component="linkButton"
                                data-event-container="navbarDropdownLink"
                                data-schema-version="1"
                                data-product-key={dataProductKey}
                                data-label={itemLabel}
                                data-label-english={itemLabelEnglish}
                            >
                                {itemLabel}
                            </a>
                        </li>
                    )
                )}
            </Fragment>
        ));

    return (
        <div
            ref={dropdownRef}
            onTouchEnd={handleTouchEnd}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onClick={handleClick}
            onFocus={handleMouseEnter}
            onKeyUp={handleKeyUp}
        >
            <a
                id={`dropdown-${label.toLowerCase()}`}
                href="#"
                data-event="clicked"
                data-label={label}
                data-event-component="linkButton"
                data-event-container="navbarDropdown"
                data-product-key={dataProductKey}
                data-schema-version="1"
                css={parentItemStyle}
                className={isMobileDropdownVisible ? "open" : ""}
            >
                {label}
            </a>
            {!isMobile && isDropdownVisible ? (
                <div css={dropdownStyle} ref={innerRef}>
                    <ul css={listStyle}>{renderGroups(dataProductKey)}</ul>
                </div>
            ) : null}
            {isMobile && isMobileDropdownVisible ? (
                <div css={mobileDropdownStyle} ref={innerRef}>
                    <ul>{renderGroups(dataProductKey)}</ul>
                </div>
            ) : null}
        </div>
    );
};

export default ProductNavDropdown;
